import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { Save, ArrowLeft } from 'lucide-react';
import { toast } from 'react-toastify';
import ReportService from '../../services/reportService';
import api from '../../services/api';

const CreateSubcontractingReport = ({
  viewMode = false,
  editMode = false,
  reportId = null,
  initialData = null
}) => {
  const navigate = useNavigate();

  const finalViewMode = !!viewMode;
  const finalEditMode = !!editMode && !finalViewMode;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    type: 'PDF',
    description: ''
  });

  // Initialize form with existing data if provided
  useEffect(() => {
  const fetchReport = async () => {
    if ((finalViewMode || finalEditMode) && reportId) {
      try {
        const response = await api.get(`/reports/${reportId}`);
        if (response.data?.success) {
          const data = response.data.data;
          setForm({
            name: data.name || '',
            type: data.type || 'PDF',
            description: data.description || ''
          });
        }
      } catch (err) {
        toast.error('Failed to fetch report');
      }
    }
  };

  fetchReport();
}, [reportId, finalViewMode, finalEditMode]);

  const handleChange = (field) => (e) => {
    if (finalViewMode) return;
    setForm((prev) => ({
      ...prev,
      [field]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (!form.name) {
        toast.error('Please enter a report name');
        setIsSubmitting(false);
        return;
      }

      const payload = {
        name: form.name,
        description: form.description,
        type: form.type,
        category: 'subcontracting'
      };

      let response;

      if (finalEditMode && reportId) {
        // Update existing report
        response = await api.put(`/reports/${reportId}`, payload);

        if (response?.data?.success) {
          toast.success(response.data.message || 'Report updated successfully');
          navigate('/subcontracting/reports');
        } else {
          toast.error(response?.data?.message || 'Failed to update report');
        }
      } else {
        // Create new report
        response = await ReportService.createReport(payload);

        if (response?.success) {
          toast.success(response.message || 'Report created successfully');
          navigate('/subcontracting/reports');
        } else {
          toast.error(response?.message || 'Failed to create report');
        }
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        err?.message ||
        'Failed to save report'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPageTitle = () => {
    if (finalViewMode) return 'View Report';
    if (finalEditMode) return 'Edit Report';
    return 'Create Report';
  };

  const getPageDescription = () => {
    if (finalViewMode) return 'View subcontracting report details';
    if (finalEditMode) return 'Update subcontracting report';
    return 'Generate a new subcontracting report';
  };

  const getSubmitButtonText = () => {
    if (finalEditMode)
      return isSubmitting ? 'Updating...' : 'Update Report';
    return isSubmitting ? 'Creating...' : 'Create Report';
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={getPageTitle()}
        description={getPageDescription()}
        actions={[]}
      />

      <div className="page-container">
        <div className="card">
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">Report Name</label>
                <input
                  value={form.name}
                  onChange={handleChange('name')}
                  className="form-control"
                  disabled={finalViewMode}
                  readOnly={finalViewMode}
                />
              </div>

              <div style={{ width: 180 }}>
                <label className="form-label">Format</label>
                <select
                  value={form.type}
                  onChange={handleChange('type')}
                  className="form-control"
                  disabled={finalViewMode}
                >
                  <option value="PDF">PDF</option>
                  <option value="Excel">Excel</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label className="form-label">Description</label>
              <textarea
                value={form.description}
                onChange={handleChange('description')}
                className="form-control"
                rows={4}
                disabled={finalViewMode}
                readOnly={finalViewMode}
              />
            </div>

            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end'
              }}
            >
              <button
                type="button"
                className="btn btn-outline"
                onClick={() =>
                  navigate('/subcontracting/reports')
                }
              >
                <ArrowLeft size={16} /> Cancel
              </button>

              {!finalViewMode && (
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSubmitting}
                >
                  <Save size={16} /> {getSubmitButtonText()}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateSubcontractingReport;